
import React, { useState, useEffect } from 'react';
import { UserPlus, Edit2, RotateCcw, Eye, UserCheck, UserX, Search } from 'lucide-react';
import { Profile } from '../../../types/user';
import { getAllUsers } from '../../../lib/userManagement';
import { getAllUserLeadStats } from '../../../lib/leadsManagement';
import ResetPasswordModal from './ResetPasswordModal';

interface UserListProps {
  onImpersonate: (userId: string) => void;
  onEditUser: (user: Profile) => void;
  onCreateUser: () => void;
  onResetPassword: (userId: string, newPassword: string) => void;
}

interface UserLeadStats {
  total: number;
  neu: number;
  kontaktiert: number;
  gesprochen: number;
  termin_fixiert: number;
  abgeschlossen: number;
  kein_interesse: number;
}

const UserList: React.FC<UserListProps> = ({ onImpersonate, onEditUser, onCreateUser, onResetPassword }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [resettingPasswordUser, setResettingPasswordUser] = useState<Profile | null>(null);
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLeadStats, setUserLeadStats] = useState<Record<string, UserLeadStats>>({});

  // Load users from Supabase
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const [fetchedUsers, stats] = await Promise.all([
      getAllUsers(),
      getAllUserLeadStats(),
    ]);
    setUsers(fetchedUsers);
    setUserLeadStats(stats);
    setLoading(false);
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeUsers = users.filter(u => u.is_active).length;
  const totalLeads = Object.values(userLeadStats).reduce((sum, stats) => sum + stats.total, 0);

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Benutzerverwaltung</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie Benutzer, Rollen und Zugriffsrechte.</p>
        </div>
        <button
          onClick={onCreateUser}
          className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all"
        >
          <UserPlus className="h-4 w-4" />
          Neuer Benutzer
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-3">
              <UserCheck className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeUsers}</p>
              <p className="text-xs font-medium text-gray-500">Aktive Benutzer</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gray-100 p-3">
              <UserX className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{users.length - activeUsers}</p>
              <p className="text-xs font-medium text-gray-500">Deaktiviert</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-3">
              <UserCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalLeads}</p>
              <p className="text-xs font-medium text-gray-500">Gesamt Anfragen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="rounded-xl bg-white p-4 shadow-md border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Benutzer suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* User Table */}
      <div className="rounded-xl bg-white shadow-md border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Benutzer
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Rolle
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Lead Stats
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Letzter Login
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-bold text-gray-900">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : user.role === 'kunde'
                            ? 'bg-green-100 text-green-700'
                            : user.role === 'caller'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {user.role === 'admin'
                        ? 'Admin'
                        : user.role === 'kunde'
                          ? 'Kunde'
                          : user.role === 'caller'
                            ? 'Caller'
                            : 'Benutzer'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2 text-xs max-w-md">
                      <span className="text-gray-600">
                        Gesamt: <span className="font-bold text-gray-900">{userLeadStats[user.id]?.total || 0}</span>
                      </span>
                      <span className="text-blue-600">
                        Neu: <span className="font-bold">{userLeadStats[user.id]?.neu || 0}</span>
                      </span>
                      <span className="text-orange-600">
                        Kont.: <span className="font-bold">{userLeadStats[user.id]?.kontaktiert || 0}</span>
                      </span>
                      <span className="text-yellow-600">
                        Gespr.: <span className="font-bold">{userLeadStats[user.id]?.gesprochen || 0}</span>
                      </span>
                      <span className="text-indigo-600">
                        Termin: <span className="font-bold">{userLeadStats[user.id]?.termin_fixiert || 0}</span>
                      </span>
                      <span className="text-green-600">
                        Abg.: <span className="font-bold">{userLeadStats[user.id]?.abgeschlossen || 0}</span>
                      </span>
                      <span className="text-red-600">
                        K. Int.: <span className="font-bold">{userLeadStats[user.id]?.kein_interesse || 0}</span>
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                        Aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-600">
                        <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                        Deaktiviert
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'Nie'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => onImpersonate(user.id)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Als Benutzer anzeigen"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onEditUser(user)}
                        className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Bearbeiten"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setResettingPasswordUser(user)}
                        className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Passwort zurücksetzen"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-gray-500">Keine Benutzer gefunden.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reset Password Modal */}
      {resettingPasswordUser && (
        <ResetPasswordModal
          user={resettingPasswordUser}
          onClose={() => setResettingPasswordUser(null)}
          onReset={onResetPassword}
        />
      )}
    </div>
  );
};

export default UserList;
